/*
 * Copyright (c) 2025 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import {
  greaterThan,
  maxStringLengthValidator,
  minNumber,
  requiredNumber,
  requiredObject,
  requiredString,
  ValidatorSchema
} from '@irian-ro/util-forms';
import { ShipmentRequestDtoModel } from '../shipment-request-dto.model';
import { isDateTimeWithTimezoneInPast } from '@irian-ro/date-utils';
import { TimeWindowDtoModel } from '@well/common-libs/time-management/domain-model';
import { ValidationErrors } from '@angular/forms';
import { Signal } from '@angular/core';
import { arePickupAndDeliveryTimeWindowsValid } from './abstract-shipment-common.validators';
import { MAXIMUM_DEFAULT_STRING_INPUT_LENGTH } from '@irian-ro/constants';

export interface ShipmentConfig {
  dateOffset: Signal<number>;
  availablePackageCount: Signal<number>;
  listShipmentEnabled: Signal<boolean>;
  isPreShipment: Signal<boolean>;
}

export class ShipmentRequestDtoValidators {
  config: ShipmentConfig;

  constructor(config: ShipmentConfig) {
    this.config = config;
  }

  get(): ValidatorSchema<ShipmentRequestDtoModel> {
    return shipmentRequestDtoValidators(this.config);
  }
}

export const shipmentRequestDtoValidators = (config: ShipmentConfig): ValidatorSchema<ShipmentRequestDtoModel> => {
  return {
    pickup: {
      address: {
        city: [requiredString],
        postalCode: [requiredString],
      },
      contact: {
        name: [maxStringLengthValidator(MAXIMUM_DEFAULT_STRING_INPUT_LENGTH)],
        phoneNumber: [maxStringLengthValidator(20)],
      },
      namedLocationVO: requiredObject,
      timeWindowDto: (dateWindow: TimeWindowDtoModel | null | undefined): ValidationErrors | null => {
        return {
          ...[
            requiredObject(dateWindow?.timeWindowStart, 'startRequired'),
            isDateTimeWithTimezoneInPast(dateWindow?.timeWindowStart, config.dateOffset()),
            isDateTimeWithTimezoneInPast(dateWindow?.timeWindowEnd, config.dateOffset())
          ]
            .filter((error) => !!error)
            .reduce((acc, error) => ({ ...acc, ...error }), {}),
        };
      },
      $omit: [
        {
          field: 'namedLocationVO',
          condition: (value: any) => !!value?.address,
        },
        {
          field: 'address',
          condition: (value: any) => !!value?.namedLocationVO,
        }
      ],
    },
    delivery: {
      address: {
        city: [requiredString],
        postalCode: [requiredString],
      },
      contact: {
        name: [maxStringLengthValidator(MAXIMUM_DEFAULT_STRING_INPUT_LENGTH)],
        phoneNumber: [maxStringLengthValidator(20)],
      },
      namedLocationVO: requiredObject,
      timeWindowDto: (dateWindow: TimeWindowDtoModel | null | undefined): ValidationErrors | null => {
        return {
          ...[
            requiredObject(dateWindow?.timeWindowStart, 'startRequired'),
            isDateTimeWithTimezoneInPast(dateWindow?.timeWindowStart, config.dateOffset()),
            isDateTimeWithTimezoneInPast(dateWindow?.timeWindowEnd, config.dateOffset())
          ]
            .filter((error) => !!error)
            .reduce((acc, error) => ({ ...acc, ...error }), {}),
        };
      },
      $omit: [
        {
          field: 'namedLocationVO',
          condition: (value: any) => !!value?.address,
        },
        {
          field: 'address',
          condition: (value: any) => !!value?.namedLocationVO,
        }
      ],
    },
    packages: {
      height: [requiredNumber, minNumber(1)],
      width: [requiredNumber, minNumber(1)],
      length: [requiredNumber, minNumber(1)],
      weight: [requiredNumber, greaterThan(0)],
      pieces: [requiredNumber, minNumber(1)],
    },
    productCustomsClearanceDTOS: {
      totalWeight: [greaterThan(0)],
    },
    shipmentVehicleDTO: {
      vehicleType: [requiredString],
    },
    $omit: [
      {
        field: 'shipmentVehicleDTO',
        condition: () => !config.isPreShipment(),
      },
      {
        field: 'packages',
        condition: (value) => config.isPreShipment(),
      },
      {
        field: 'productCustomsClearanceDTOS',
        condition: (value) => true,
      }
    ],
    $cross: [
      (shipment: any) => arePickupAndDeliveryTimeWindowsValid(shipment),
      (shipment: any) => {
        return config.isPreShipment()
          ? null
          : shipment?.packages && Object.keys(shipment.packages).length
          ? null
          : { packageRequired: true };
      },
      (shipment: any) => {
        return shipment?.pickup && (shipment.pickup.namedLocationVO || shipment.pickup.address)
          ? null
          : { pickupLocationRequired: true };
      },
      (shipment: any) => {
        return shipment?.delivery && (shipment.delivery.namedLocationVO || shipment.delivery.address)
          ? null
          : { deliveryLocationRequired: true };
      },
      (shipment) => {
        if (shipment) {
          return packageCountExceededValidator(shipment, config.availablePackageCount(), config.listShipmentEnabled());
        }
        return null;
      }
    ],
  };
};

export const packageCountExceededValidator = (
  shipment: ShipmentRequestDtoModel,
  availablePackageCount: number,
  listShipmentEnabled: boolean
) => {
  if (listShipmentEnabled && availablePackageCount < 0) {
    return { packageCountExceeded: true };
  } else if (listShipmentEnabled) {
    return null;
  }

  const totalPackagesForCurrentShipment = Object.values(shipment?.packages || {}).reduce(
    (acc, pkg) => acc + (pkg?.pieces || 0),
    0
  );

  return totalPackagesForCurrentShipment > availablePackageCount ? { packageCountExceeded: true } : null;
};
